import { msg } from "../../locales/translate";
import Signal from "badmfck-signal";
import { GD } from "src/GD";
import type { ITerminal } from "src/types/types";
import type { TIntegration } from "../pages/IntegrationsList";
import type { TPaymentProvider } from "../providers/ProvidersList";

export type TBeneficiary = Record<string, any> & {
	id: number;
	name?: string;
	iban?: string;
	bic?: string;
	bank?: string;
	status?: string;
	raw?: string;
};

export type TPaymentChannel = {
	id: number;
	terminal_name: string;
	terminal_type: string;
	currency: string;
	name: string;
	target_id: string;
	target_type: string;
	instrument_type: string;
	parent_name: string;
	status: string;
	terminal: ITerminal;
	integration?: TIntegration;
	beneficiary?: TBeneficiary;
	beneficiaries?: TBeneficiary[];
	provider: TPaymentProvider;
	linked: {
		channel_id: number;
		merchant_id: number;
		created_at: string;
		project_id: number;
		id: number;
		order: number;
		alias: string;
		deletable: number;
		status: string;
		processing_mid: string;
	};
	actual_fees?: { client: any; setup: any; integration: any; channel: any };
};

export const S_CHANNELS_LIST_REQUEST_UPDATE = new Signal<void>("S_CHANNELS_LIST_REQUEST_UPDATE");
export const S_CHANNEL_ITEM_POPUP = new Signal<TPaymentChannel | null>("S_CHANNEL_ITEM_POPUP");

export const getChannelsForProviderMethod = (provider?: TPaymentProvider, method?: ITerminal): IAPIRequest => {
	return {
		method: "payments/channels/list",
		data: {
			provider_id: provider?.id,
			terminal_id: method?.id,
		},
	};
};

export const getChannelsForMerchant = (merchant_id?: number, method_id?: number): IAPIRequest => {
	return {
		method: "payments/channels/merchant/list",
		data: {
			merchant_id: merchant_id,
			terminal_id: method_id,
		},
	};
};

export const editMerchantChannel = async (data: { alias: string; mid: string; linked_id: number; order: number }): Promise<IAPIResponse> => {
	if (!data.alias || !data.mid) return { error: { code: -1, message: msg("aliasAndMidAreRequired") } };

	const response = await GD.REQ_API.request({
		method: "payments/channel/linked/edit",
		data: {
			linked_id: data.linked_id,
			order: data.order,
			alias: data.alias,
			mid: data.mid,
		},
	});
	return response;
};

export const normalizeBeneficiary = (beneficiary: TBeneficiary): TBeneficiary => {
	if (!beneficiary.raw || typeof beneficiary.raw !== "string") return beneficiary;

	try {
		const raw = JSON.parse(beneficiary.raw);
		if (!raw || typeof raw !== "object") return beneficiary;
		return {
			...raw,
			...beneficiary,
		};
	} catch (_e) {
		return beneficiary;
	}
};

const getChannelIdForBeneficiaryLink = (channel: TPaymentChannel) => channel.linked?.id ?? channel.id;

export const getBeneficiaryLinkData = (channel: TPaymentChannel, beneficiaryId: number) => ({
	beneficiary_id: beneficiaryId,
	channel_id: getChannelIdForBeneficiaryLink(channel),
	...(channel.linked?.merchant_id ? { merchant_id: channel.linked.merchant_id } : {}),
});

export const getChannelBeneficiaries = (channel: TPaymentChannel): TBeneficiary[] => {
	if (Array.isArray(channel.beneficiaries)) return channel.beneficiaries.map(normalizeBeneficiary);
	if (channel.beneficiary) return [normalizeBeneficiary(channel.beneficiary)];
	if (Array.isArray((channel.linked as any)?.beneficiaries)) return (channel.linked as any).beneficiaries.map(normalizeBeneficiary);
	if ((channel.linked as any)?.beneficiary) return [normalizeBeneficiary((channel.linked as any).beneficiary)];
	if ((channel.linked as any)?.beneficiary_id) {
		return [
			normalizeBeneficiary({
				id: (channel.linked as any).beneficiary_id,
				name: (channel.linked as any).beneficiary_name,
				status: (channel.linked as any).beneficiary_status,
			}),
		];
	}
	return [];
};

export const isSamePaymentChannel = (a?: TPaymentChannel | null, b?: TPaymentChannel | null) => {
	if (!a || !b) return false;
	if (a.linked?.id && b.linked?.id) return a.linked.id === b.linked.id;
	if (!!a.linked !== !!b.linked) return false;
	if (a.linked?.merchant_id && b.linked?.merchant_id) return a.id === b.id && a.linked.merchant_id === b.linked.merchant_id;
	return a.id === b.id;
};

const getBeneficiarySignature = (channel: TPaymentChannel) => {
	return getChannelBeneficiaries(channel)
		.map(beneficiary => `${beneficiary.id}:${beneficiary.status ?? ""}:${beneficiary.name ?? ""}`)
		.sort()
		.join("|");
};

export const getChannelSignature = (channel: TPaymentChannel) => {
	return [
		channel.id,
		channel.status,
		channel.name,
		channel.currency,
		channel.terminal?.id,
		channel.terminal?.type,
		channel.provider?.id,
		channel.provider?.name,
		channel.integration?.id,
		channel.integration?.name,
		channel.integration?.status,
		channel.linked?.id,
		channel.linked?.status,
		channel.linked?.alias,
		channel.linked?.processing_mid,
		getBeneficiarySignature(channel),
	].join("|");
};

export const getChannelsFromResponse = (response: IAPIResponse): TPaymentChannel[] => {
	const data = response.data as any;
	if (!data) return [];
	if (Array.isArray(data)) return data;
	if (Array.isArray(data.channels)) return data.channels;
	if (Array.isArray(data.data)) return data.data;
	if (Array.isArray(data.data?.channels)) return data.data.channels;
	return [];
};

const getUpdatedChannelFromMerchantList = async (channel: TPaymentChannel): Promise<TPaymentChannel | null> => {
	if (!channel.linked?.merchant_id) return null;

	const response = await GD.REQ_API.request(getChannelsForMerchant(channel.linked.merchant_id, channel.terminal?.id));
	if (response.error) return null;

	const channels = getChannelsFromResponse(response);
	return channels.find((item: TPaymentChannel) => isSamePaymentChannel(item, channel)) ?? null;
};

/**
 * Reads the channel again from the list it came from. `payments/channel/get` is not used: for an existing id the
 * server returns the provider's first channel, which is a different channel whenever the provider has several.
 */
export const refreshPaymentChannel = async (channel: TPaymentChannel): Promise<TPaymentChannel | null> => {
	if (channel.linked) return getUpdatedChannelFromMerchantList(channel);

	const response = await GD.REQ_API.request({
		method: "payments/channels/list",
		data: {
			id: channel.id,
			provider_id: channel.provider?.id,
		},
	});

	if (response.error) return null;
	return getChannelsFromResponse(response).find(item => item.id === channel.id) ?? null;
};

export const setChannelBeneficiaries = (channel: TPaymentChannel, beneficiaries: TBeneficiary[]): TPaymentChannel => ({
	...channel,
	beneficiary: beneficiaries[0],
	beneficiaries,
	linked: channel.linked
		? ({
				...channel.linked,
				beneficiary: beneficiaries[0],
				beneficiaries,
				beneficiary_id: beneficiaries[0]?.id,
				beneficiary_name: beneficiaries[0]?.name,
				beneficiary_status: beneficiaries[0]?.status,
			} as TPaymentChannel["linked"])
		: channel.linked,
});

export const mergePaymentChannel = (base: TPaymentChannel, update: TPaymentChannel, beneficiarySource: "server" | "base" | "none" = "server"): TPaymentChannel => {
	const beneficiaries = beneficiarySource === "base" ? getChannelBeneficiaries(base) : beneficiarySource === "server" ? getChannelBeneficiaries(update) : [];
	const nextChannel = {
		...base,
		...update,
		terminal_name: update.terminal_name ?? base.terminal_name,
		terminal_type: update.terminal_type ?? base.terminal_type,
		currency: update.currency ?? base.currency,
		name: update.name ?? base.name,
		target_id: update.target_id ?? base.target_id,
		target_type: update.target_type ?? base.target_type,
		instrument_type: update.instrument_type ?? base.instrument_type,
		parent_name: update.parent_name ?? base.parent_name,
		status: update.status ?? base.status,
		terminal: update.terminal ?? base.terminal,
		// A disconnected integration is absent from the fresh record, so it must not fall back to the old one.
		integration: update.integration,
		provider: update.provider ?? base.provider,
		linked:
			base.linked || update.linked
				? ({
						...(base.linked ?? {}),
						...(update.linked ?? {}),
					} as TPaymentChannel["linked"])
				: update.linked,
	};

	return setChannelBeneficiaries(nextChannel, beneficiaries);
};

export const isOpenBankingChannel = (channel: TPaymentChannel) => {
	const values = [
		channel.terminal_type,
		channel.terminal?.type,
		channel.instrument_type,
		channel.name,
		channel.terminal_name,
		channel.terminal?.name,
		channel.target_type,
	].map(value => (value ?? "").toString().toLowerCase().trim());

	return values.some(value => value === "ob" || value === "open_banking" || value === "open banking" || value.includes("open banking"));
};

/** Fee records of a channel belong to the merchant link when there is one, otherwise to the provider channel. */
export const channelFeesTarget = (channel: TPaymentChannel) => ({
	target_id: channel.linked ? channel.linked.id : channel.id,
	target_type: (channel.linked ? "merchant" : "provider") as "merchant" | "provider",
});
